import { z } from 'zod'
import { squareName, uciMove } from './chess.ts'
import { puzzleId } from './puzzle.ts'
import { userId } from './user.ts'

export const attemptId = z.uuid()
export type AttemptId = z.infer<typeof attemptId>

export const attemptOutcome = z.enum(['solved', 'failed', 'abandoned'])
export type AttemptOutcome = z.infer<typeof attemptOutcome>

export const ratingChange = z.object({
  before: z.number().int(),
  after: z.number().int(),
  delta: z.number().int(),
})

export type RatingChange = z.infer<typeof ratingChange>

/**
 * An attempt is an immutable event: one user, one puzzle, one sitting. Ratings
 * are snapshotted onto the row so history stays replayable even if the rating
 * formula changes later.
 */
export const attempt = z.object({
  id: attemptId,
  userId,
  puzzleId,
  outcome: attemptOutcome.nullable(),
  movesPlayed: z.array(uciMove),
  hintsUsed: z.number().int().min(0),
  msElapsed: z.number().int().min(0).nullable(),
  userRatingBefore: z.number().int(),
  userRatingAfter: z.number().int().nullable(),
  puzzleRatingAtAttempt: z.number().int(),
  startedAt: z.date(),
  finishedAt: z.date().nullable(),
})

export type Attempt = z.infer<typeof attempt>

export const submitMoveInput = z.object({
  attemptId,
  move: uciMove,
  /**
   * How many moves of the line the client believes it has already found.
   *
   * The server checks this against its own count and refuses a mismatch. A
   * duplicate submission — two handlers firing for one gesture, a retried
   * request — then arrives with a stale index and is rejected outright, rather
   * than being judged against the *next* move in the line and silently scored
   * as a wrong answer.
   */
  movesMade: z.number().int().min(0),
})

/**
 * The three ways a submitted move can land. A `correct` move is never the last
 * one — if it were, the opponent would have no reply and the status would be
 * `solved` — so `reply` is always present.
 */
export const moveResult = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('correct'),
    /** The opponent's answer, for the client to animate. */
    reply: uciMove,
    /** Player moves still to find. */
    movesRemaining: z.number().int().min(1),
  }),
  z.object({
    status: z.literal('solved'),
    rating: ratingChange,
    msElapsed: z.number().int().min(0),
  }),
  z.object({
    status: z.literal('incorrect'),
    /** Revealed once the attempt is spent, so the user learns something. */
    solution: z.array(uciMove),
    rating: ratingChange,
  }),
])

export type MoveResult = z.infer<typeof moveResult>

export const hintInput = z.object({ attemptId })

export const hintResult = z.object({
  /** The square holding the piece that wants to move. */
  from: squareName,
  hintsUsed: z.number().int().min(1),
})

export type HintResult = z.infer<typeof hintResult>
